import CardValidator from 'card-validator';
import { differenceInCalendarMonths } from 'date-fns';

import { CartPaymentCardType, LoyaltyPaymentMethod } from 'generated/graphql-gateway';
import { CartPaymentType } from 'generated/graphql-gateway';
import { t } from 'state/intl/translations';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { IUseCartTip, TipAmounts } from 'state/order/hooks/use-cart-tip';
import {
  ALL_COUNTRIES_ZIP_CODE_REGEX,
  COUNTRY_BASED_ZIP_CODE_REGEX,
  isMappedCountryBasedZipCodeRegex,
  sanitizeAlphanumeric,
  sanitizeNumber,
} from 'utils/form';
import { ISOs } from 'utils/form/constants';
import { IPlaceAddress } from 'utils/geolocation/types';
import { getBillingCountryError } from 'utils/intl/get-billing-country-error';

import {
  CardType,
  CurrentSelectedMethodType,
  ICredit,
  IPaymentErrors,
  IPaymentMethod,
  IPaymentState,
  IRevaultInformation,
  PaymentMethodCategories,
} from './types';
import { CCFormField, CardTypes, IPaymentPayload } from './types';

type CCFormAddress = Pick<
  IPaymentState,
  'billingStreetAddress' | 'billingApt' | 'billingCity' | 'billingState' | 'billingZip'
>;

export const amexpRegEx: RegExp = /^3[47][0-9]{13}$/;

//per: https://github.com/braintree/credit-card-type#api
const mapValidatorResultToAppCardTypes = {
  'american-express': CartPaymentCardType.AMEX,
  'diners-club': CartPaymentCardType.DINERS_CLUB,
  discover: CartPaymentCardType.DISCOVER,
  jcb: CartPaymentCardType.JCB,
  mastercard: CartPaymentCardType.MASTERCARD,
  visa: CartPaymentCardType.VISA,
  // our app and LD flags for accepted payment types seems to consider discover and union pay the same thing so do this mapping
  unionpay: CartPaymentCardType.DISCOVER,
};

export const isApplePay = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.APPLE_PAY;

export const isGooglePay = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.GOOGLE_PAY;

export const isPayPal = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.PAYPAL;

export const isCash = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.CASH;

export const isVenmo = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.VENMO;

export const isCashApp = (accountIdentifier?: string | null) =>
  accountIdentifier === CartPaymentType.CASHAPP;

export const getPaymentMethodId = (paymentMethod: IPaymentMethod) =>
  paymentMethod.accountIdentifier ?? paymentMethod.fdAccountId ?? '';

export const isCreditCardPaymentMethod = (method: IPaymentMethod) =>
  method.credit &&
  method.credit.cardType !== CardTypes.APPLE_PAY &&
  method.credit.cardType !== CardTypes.GOOGLE_PAY;

export const isCreditCardAndGoogleApplePaymentMethod = (method: IPaymentMethod) =>
  method.credit && method.accountIdentifier !== CurrentSelectedMethodType.ADD_NEW_CARD;

export const getSortedMethods = (methods: IPaymentMethod[]) => {
  return methods.sort((a, b) => {
    const aIsCreditCard = isCreditCardPaymentMethod(a);
    const bIsCreditCard = isCreditCardPaymentMethod(b);

    if (aIsCreditCard && !bIsCreditCard) {
      return 1;
    }
    if (!aIsCreditCard && bIsCreditCard) {
      return -1;
    }
    return 0;
  });
};

export const classifyPaymentMethods = (
  methods: IPaymentMethod[],
  selectedMethodId: string
): PaymentMethodCategories =>
  methods.reduce<PaymentMethodCategories>(
    (methodsCategories, method) => {
      const isSelectedMethod = getPaymentMethodId(method) === selectedMethodId;
      const isPrepaidMethod = Boolean(method.prepaid);

      return {
        selectedMethod: isSelectedMethod ? method : methodsCategories.selectedMethod,
        prepaidPaymentMethod: isPrepaidMethod ? method : methodsCategories.prepaidPaymentMethod,
        unSelectedMethods: isSelectedMethod
          ? methodsCategories.unSelectedMethods
          : [...methodsCategories.unSelectedMethods, method],
      };
    },
    {
      selectedMethod: undefined,
      prepaidPaymentMethod: undefined,
      unSelectedMethods: [],
    }
  );

/**
 * Validates credit cart expiration date (year and month)
 * @param cardInformation ICredit, object with card information
 * @returns boolean
 */
export const isCreditCardNotExpired = (cardInformation?: ICredit | null): boolean => {
  const { expiryYear: expYr, expiryMonth: expMon } = cardInformation || {};
  if (!expYr || !expMon) {
    return false;
  }

  const currentDate = new Date();
  const expirationDate = new Date(2000 + parseInt(expYr, 10), parseInt(expMon, 10));

  return expirationDate > currentDate;
};

/**
 * useful for excluding certain payment methods from being used to quick pay
 * @param {IPaymentMethod} paymentMethod
 * @returns {boolean} whether this can be used for quick pay
 */
export const isAllowedQuickPayMethod = (paymentMethod: IPaymentMethod) =>
  !(
    paymentMethod.cash ||
    paymentMethod.paypal ||
    paymentMethod.venmo ||
    paymentMethod.prepaid ||
    getPaymentMethodId(paymentMethod) === CartPaymentCardType.APPLE_PAY ||
    getPaymentMethodId(paymentMethod) === CartPaymentCardType.GOOGLE_PAY ||
    paymentMethod.cashapp
  );

/**
 * useful for excluding certain payment methods from being used to reload prepaid cards or quick pay
 * @param {IPaymentMethod} paymentMethod
 * @returns {boolean} whether this can be used for reloading a prepaid card or quick pay
 */
export const isAllowedReloadMethod = (paymentMethod: IPaymentMethod) =>
  !paymentMethod.prepaid &&
  !paymentMethod.cash &&
  !paymentMethod.paypal &&
  !paymentMethod.venmo &&
  !paymentMethod.cashapp;

export function isGiftCardNumberValid(cardNumber: string): boolean {
  return /^[0-9]{16}$/.test(cardNumber);
}

const isValidAppCardType = (
  key: string | undefined
): key is keyof typeof mapValidatorResultToAppCardTypes =>
  !!key && Object.keys(mapValidatorResultToAppCardTypes).includes(key);

export function validateCreditCardNumber(
  cardNumber: string,
  supportedCardTypes: CardType[]
): { isValid: boolean; cardType?: CartPaymentCardType | null; niceCardType?: string } {
  const result = CardValidator.number(cardNumber);
  const cardType = isValidAppCardType(result?.card?.type)
    ? mapValidatorResultToAppCardTypes[result.card.type] ?? null
    : null;

  const resp = {
    isValid: Boolean(cardType && supportedCardTypes.includes(cardType) && result.isValid),
    cardType,
    niceCardType: result?.card?.niceType,
  };

  return resp;
}

export const splitExpiry = (expiry: string) => {
  const [expiryMonth = '', expiryYear = '']: string[] = expiry.split('/');

  return {
    expiryMonth,
    expiryYear,
  };
};

function validateExpiry(expiryMonthAndYear: string): boolean {
  const { expiryMonth, expiryYear } = splitExpiry(expiryMonthAndYear);
  const monthNumber = Number(expiryMonth);
  if (monthNumber < 1 || monthNumber > 12) {
    return false;
  }
  const now: Date = new Date();
  const expiry: Date = new Date(Number(`20${expiryYear}`), monthNumber - 1);
  return differenceInCalendarMonths(expiry, now) >= 0;
}

// TODO: move to another utils
export function excludeNumeric(string: string): string {
  return string.replace(/^\d$/g, '').trim();
}

export const onCCFormChange = (
  name: CCFormField,
  value: string,
  incomingState: IPaymentState,
  formErrors: IPaymentErrors,
  supportedCardTypes: CardType[]
): { state: IPaymentState; formErrors: IPaymentErrors } => {
  // Make a copy to avoid mutating react state / the original argument
  const state = { ...incomingState };

  switch (name) {
    case 'cardNumber': {
      const sanitizedNumber = sanitizeNumber(value);

      const { cardType, isValid, niceCardType } = validateCreditCardNumber(
        sanitizedNumber,
        supportedCardTypes
      );

      formErrors.cardType = '';

      if (!cardType && niceCardType) {
        // support saying invalid card type based on libraries knowledge of many unsupported card types
        formErrors.cardType = t('unsupportedCardType', { cardType: niceCardType });
      }

      if (isValid) {
        formErrors.cardNumber = '';
      }

      state.cardNumber = sanitizedNumber;
      state.cardType = cardType ?? null;
      state.isCardNumberValid = isValid;
      break;
    }
    case 'expiry':
      formErrors.expiry = '';
      state.expiry = value;
      state.isExpiryValid = validateExpiry(value);
      break;
    case 'billingZip':
      formErrors.billingZip = '';
      state.billingZip = value.toUpperCase();
      break;
    case 'billingAddressSameAsDelivery':
      state.billingAddressSameAsDelivery = !state.billingAddressSameAsDelivery;
      if (!state.billingAddressSameAsDelivery) {
        state.billingStreetAddress = '';
        state.billingApt = '';
        state.billingCity = '';
        state.billingState = '';
        state.billingZip = '';
      } else {
        formErrors.billingZip = '';
      }
      break;
    case 'saveCard':
      state.saveCard = !state.saveCard;
      break;
    case 'nameOnCard':
      formErrors.nameOnCard = '';
      state.nameOnCard = value;
      break;
    case 'cvv':
      formErrors.cvv = '';
      state.cvv = value;
      break;
    case 'billingStreetAddress':
      formErrors.billingStreetAddress = '';
      state.billingStreetAddress = value;
      break;
    case 'billingApt':
      formErrors.billingApt = '';
      state.billingApt = value;
      break;
    case 'billingCity':
      formErrors.billingCity = '';
      state.billingCity = value;
      break;
    case 'billingState':
      formErrors.billingState = '';
      state.billingState = value;
      break;
    case 'billingCountry':
      formErrors.billingCountry = '';
      state.billingCountry = value as ISOs;
      break;
  }
  return { state, formErrors };
};

const validateCardZipCode = (zipCode: string, country: string, countryFieldEnabled: boolean) => {
  let cardZipCodeError;
  const regex =
    countryFieldEnabled && isMappedCountryBasedZipCodeRegex(country)
      ? COUNTRY_BASED_ZIP_CODE_REGEX[country]
      : ALL_COUNTRIES_ZIP_CODE_REGEX;

  if (zipCode && country && !regex.test(zipCode)) {
    const errorKey = country === ISOs.USA ? 'zipCodeInvalid' : 'postalCodeInvalid';
    cardZipCodeError = t(errorKey);
  }

  return cardZipCodeError;
};

export const getCCFormErrors = (
  state: IPaymentState,
  formErrors: IPaymentErrors,
  paymentFieldVariations: PaymentFieldVariations = defaultPaymentFieldVariation,
  country: ISOs
): { hasErrors: boolean; formErrors: IPaymentErrors } => {
  if (!state.isCardNumberValid) {
    formErrors.cardNumber = t('ccNumberIsNotValid');
  }
  if (!state.cvv || state.cvv.trim().length < 3) {
    formErrors.cvv = t('cvvMustBeAtLeast3Digits');
  }
  if (!state.isExpiryValid) {
    formErrors.expiry = t('creditCardExpired');
  }
  if (!state.expiry?.trim()) {
    formErrors.expiry = t('expirationDateIsRequired');
  }
  if (paymentFieldVariations.name && !state.nameOnCard?.trim()) {
    formErrors.nameOnCard = t('nameOnCardIsRequired');
  }
  if (!state.cardNumber.trim()) {
    formErrors.cardNumber = t('cardNumberIsRequired');
  }
  if (paymentFieldVariations.cvv && !state.cvv?.trim()) {
    formErrors.cvv = t('cvvIsRequired');
  }
  if (paymentFieldVariations.addressLine1 && !state.billingStreetAddress?.trim()) {
    formErrors.billingStreetAddress = t('addressRequiredError');
  }
  if (paymentFieldVariations.city && !state.billingCity?.trim()) {
    formErrors.billingCity = t('cityRequiredError');
  }
  if (paymentFieldVariations.state && !state.billingState?.trim()) {
    formErrors.billingState = t('stateIsARequiredField');
  }
  if (paymentFieldVariations.zip) {
    const billingZip = state.billingZip.trim();
    const countryToValidateBy = paymentFieldVariations.country ? state.billingCountry : country;
    // Validate zip code based on country if country input is displayed
    if (billingZip && countryToValidateBy) {
      const billingZipError = validateCardZipCode(
        state.billingZip,
        countryToValidateBy,
        paymentFieldVariations.country
      );
      if (billingZipError) {
        formErrors.billingZip = billingZipError;
      }
    }

    if (!billingZip) {
      formErrors.billingZip =
        countryToValidateBy === ISOs.USA ? t('zipCodeRequiredError') : t('postalCodeRequiredError');
    }
  }
  if (paymentFieldVariations.country && !state.billingCountry) {
    formErrors.billingCountry = getBillingCountryError();
  }
  // set didAttemptSubmit as false since we are using errors for submit error tracking too
  const hasErrors: boolean = Object.values({ ...formErrors, didAttemptSubmit: false }).some(
    err => err
  );

  return { hasErrors, formErrors };
};

export const getFraudPreventionValues = ({
  cardNumber,
  nameOnCard,
  billingStreetAddress,
  billingApt,
  billingCity,
  billingState,
  billingZip,
  billingCountry,
  expiry,
}: IPaymentState) => {
  const ccBin: string | undefined = cardNumber ? cardNumber.slice(0, 6) : undefined;
  const ccLast4: string | undefined = cardNumber
    ? cardNumber.slice(-4, cardNumber.length)
    : undefined;

  return {
    billingAddress: {
      country: billingCountry,
      unitNumber: billingApt,
      locality: billingCity,
      postalCode: billingZip,
      region: billingState,
      streetAddress: billingStreetAddress,
    },
    fullName: nameOnCard,
    ccBin,
    ccLast4,
    expiry,
  };
};

export const mapDeliveryToCCFormAddress = (
  deliveryAddress: Partial<IPlaceAddress> | null
): CCFormAddress => ({
  billingStreetAddress: deliveryAddress?.addressLine1 || '',
  billingApt: deliveryAddress?.addressLine2 || '',
  billingCity: deliveryAddress?.city || '',
  billingState: deliveryAddress?.state || '',
  billingZip: deliveryAddress?.zip || '',
});

/**
 * This function check whether the selected payment method needs to be re-vaulted (default: false)
 */
export const shouldReVault = ({
  accountIdentifier,
  isOrbitalPaymentEnabled,
  methods,
}: {
  accountIdentifier: string;
  isOrbitalPaymentEnabled: boolean;
  methods: IPaymentMethod[];
}): IRevaultInformation => {
  // Get the clicked payment method object
  const clickedPaymentMethod = methods.find(method => {
    const selectedAccountIdentifier = method.accountIdentifier ?? method.fdAccountId ?? '';
    return selectedAccountIdentifier === accountIdentifier;
  });

  return shouldReVaultPaymentMethod(clickedPaymentMethod, isOrbitalPaymentEnabled);
};

export const shouldReVaultPaymentMethod = (
  method: IPaymentMethod | undefined,
  isOrbitalPaymentEnabled?: boolean
): IRevaultInformation => {
  const accountIdentifier = method?.accountIdentifier || method?.fdAccountId;
  const isDigitalPayment =
    isApplePay(accountIdentifier) ||
    isGooglePay(accountIdentifier) ||
    isPayPal(accountIdentifier) ||
    isVenmo(accountIdentifier);

  const isCashPayment = isCash(accountIdentifier);

  const isPaymentMethodValid = validatePaymentMethod({
    isDigitalPayment,
    isCashPayment,
    isOrbitalPaymentEnabled: Boolean(isOrbitalPaymentEnabled),
    clickedPaymentMethod: method,
  });

  const isCreditCardExpirationValid =
    method && isCreditCardPaymentMethod(method) ? isCreditCardNotExpired(method.credit) : true;

  return { isInvalid: !isPaymentMethodValid, isExpired: !isCreditCardExpirationValid };
};

/**
 * This function check if the tip enter by the user is valid
 */
export const checkIfValidTip = ({
  tipAmount,
  subTotalCents,
}: {
  tipAmount?: IUseCartTip['tipAmount'];
  subTotalCents?: number;
}) => {
  const isTipLessThanSubTotal = Number(tipAmount) <= Number(subTotalCents);
  const isTipLessThanMax = Number(tipAmount) <= TipAmounts.MAX_AMOUNT;
  return !!(isTipLessThanSubTotal && isTipLessThanMax);
};

export const extractPaymentTypeForLD = (paymentMethod: IPaymentMethod | undefined) => {
  const isApplePayPaymentType = paymentMethod?.fdAccountId === CartPaymentCardType.APPLE_PAY;
  const isGooglePayPaymentType = paymentMethod?.fdAccountId === CartPaymentCardType.GOOGLE_PAY;
  const isCreditCardPaymentType =
    !!paymentMethod?.credit && !isApplePayPaymentType && !isGooglePayPaymentType;
  const isCashPaymentPaymentType = paymentMethod?.accountIdentifier === 'CASH';
  let ldPaymentType;
  if (isCashPaymentPaymentType) {
    ldPaymentType = CartPaymentCardType.CASH;
  } else if (isCreditCardPaymentType) {
    ldPaymentType = CartPaymentCardType.CREDIT;
  } else if (isApplePayPaymentType) {
    ldPaymentType = CartPaymentCardType.APPLE_PAY;
  } else if (isGooglePayPaymentType) {
    ldPaymentType = CartPaymentCardType.GOOGLE_PAY;
  }
  return ldPaymentType;
};

export const mapPaymentMethodToLoyaltyPaymentMethods = (
  paymentMethod?: IPaymentMethod | null
): LoyaltyPaymentMethod | null => {
  if (!paymentMethod) {
    return null;
  }

  let backendPaymentMethod = null;
  const paymentType = extractPaymentTypeForLD(paymentMethod);

  if (paymentType) {
    const loyaltyRecord = LoyaltyPaymentMethod as Record<string, string | undefined>;
    if (paymentType === CartPaymentCardType.CREDIT && paymentMethod.credit) {
      backendPaymentMethod =
        (loyaltyRecord[paymentMethod.credit.cardType] as LoyaltyPaymentMethod) ?? null;
    } else {
      backendPaymentMethod = (loyaltyRecord[paymentType] as LoyaltyPaymentMethod) ?? null;
    }
  }

  return backendPaymentMethod ?? null;
};

/**
 * Checks if payment method is valid
 * @param isDigitalPayment boolean
 * @param isCashPayment boolean
 * @param isOrbitalPaymentEnabled boolean
 * @param clickedPaymentMethod IPaymentMethod
 * @returns boolean return true if valid payment method, false otherwise
 */
export const validatePaymentMethod = ({
  isDigitalPayment,
  isCashPayment,
  isOrbitalPaymentEnabled,
  clickedPaymentMethod,
}: {
  isDigitalPayment: boolean;
  isCashPayment: boolean;
  isOrbitalPaymentEnabled: boolean;
  clickedPaymentMethod: IPaymentMethod | undefined;
}): boolean => {
  // If Orbital payment is enabled and orbitalIdentifier is missing in
  // the payment method, then it is considered invalid and a re-vaulting is required
  if (!isDigitalPayment && !isCashPayment && isOrbitalPaymentEnabled) {
    return (
      Boolean(clickedPaymentMethod?.credit) && Boolean(clickedPaymentMethod?.orbitalIdentifier)
    );
  }

  return true;
};

const fallbackPaymentMethodCardTypes: Record<string, CardTypes> = {
  mc: CardTypes.MASTERCARD,
  visa: CardTypes.VISA,
  amex: CardTypes.AMEX,
  discover: CardTypes.DISCOVER,
  jcb: CardTypes.JCB,
  diners: CardTypes.DINERS_CLUB,
};

const isValidCardType = (cardType: string | undefined | null): cardType is CardTypes => {
  return !!cardType && Object.keys(CardTypes).includes(cardType.toUpperCase());
};

/**
 * We are attempting to map our payment processor cardTypes to our FE CardType's.
 *
 * NOTE: This is intended to map the returned cardType for credit payment methods from the userAccounts query.
 */
export const mapPaymentMethodCardTypes = (
  paymentMethodCardType: string | undefined | null
): CardTypes | null => {
  if (!paymentMethodCardType) {
    return null;
  }

  const cardTypeUpperCased = paymentMethodCardType.toUpperCase();

  // check for all original card types, mostly first data values
  return isValidCardType(cardTypeUpperCased)
    ? CardTypes[cardTypeUpperCased]
    : // check for payment processor card types
      // NOTE: in the future we may need to expand this
      fallbackPaymentMethodCardTypes[paymentMethodCardType] ?? null;
};

/**
 * Map CartPaymentCardType to LoyaltyPaymentMethod.
 */
export const mapCardTypeToLoyalty = (
  cardType: CartPaymentCardType | null
): LoyaltyPaymentMethod | null => {
  if (!cardType) {
    return null;
  }

  switch (cardType) {
    case CartPaymentCardType.DINERS_CLUB:
      return LoyaltyPaymentMethod.DINERSCLUB;
    case CartPaymentCardType.DINERS:
      return null;
    default:
      return LoyaltyPaymentMethod[cardType];
  }
};

export const transformPaymentValues = (
  {
    paymentValues,
    paymentFieldVariations = defaultPaymentFieldVariation,
  }: {
    paymentValues: IPaymentState | undefined;
    paymentFieldVariations?: PaymentFieldVariations;
  },
  hiddenFieldValue: string | null = null
): IPaymentPayload | undefined => {
  if (!paymentValues) {
    return;
  }
  let expiryDate: { month: string; year: string } | null = null;
  const cardNumber = sanitizeNumber(paymentValues.cardNumber);
  const { expiryMonth, expiryYear } = splitExpiry(paymentValues.expiry ?? '');
  expiryDate = paymentValues.expiry
    ? {
        month: expiryMonth,
        year: expiryYear,
      }
    : null;

  const country = paymentValues.billingCountry ? ISOs[paymentValues.billingCountry] : null;
  const postalCode = sanitizeAlphanumeric(paymentValues.billingZip);
  return {
    billingAddress: {
      country: paymentFieldVariations.country ? country : (hiddenFieldValue as ISOs),
      postalCode: paymentFieldVariations.zip ? postalCode : '',
      unitNumber: paymentFieldVariations.addressLine2 ? paymentValues.billingApt : hiddenFieldValue,
      locality: paymentFieldVariations.city ? paymentValues.billingCity : hiddenFieldValue,
      region: paymentFieldVariations.state ? paymentValues.billingState : hiddenFieldValue,
      streetAddress: paymentFieldVariations.addressLine1
        ? paymentValues.billingStreetAddress
        : hiddenFieldValue,
    },
    cardNumber,
    cardType: paymentValues.cardType ?? '',
    expiryDate,
    nameOnCard: paymentValues.nameOnCard,
    securityCode: paymentValues.cvv,
  };
};
